import { CommonModule } from '@angular/common';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, Injectable, Injector, Input, NgModule, OnChanges, Output, ViewChild, ViewEncapsulation, effect, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { ActivityEntityName, Attachment, DocumentThemeAny, DocumentThemeEntities, DocumentThemeScopeRequest, Entity } from '@jms/model';
import { PickerItemModule, PickerItemStyle } from '@jms/ui/ui.picker.item';
import { PickerBaseComponent, PickerFetch } from '@jms/ui/ui.pickers.base';
import { onDownloadError, FilenameExtractService } from '@jms/utils/util.download';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ButtonIconComponent } from '@workbuddy/ui/v2/components/button';
import { ToastService } from '@workbuddy/ui/services';
import saveAs from 'file-saver';
import * as Enumerable from 'linq';
import { catchError, EMPTY, fromEvent, map, merge, Observable, of, Subscription, switchMap, take, tap } from 'rxjs';
import { DocumentThemePickerItem } from './models';

export class DocumentDownloadContext {
  public entity: Entity;
  public fileName?: string;
}

@UntilDestroy()
@Component({
  selector: 'document-theme-picker',
  templateUrl: './document.theme.picker.component.html',
  styleUrls: ['./document.theme.picker.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: DocumentThemePickerComponent, multi: true }
  ],
  standalone: false
})
export class DocumentThemePickerComponent extends PickerBaseComponent implements OnChanges {

  @Input() public placeholder = '<Document Theme>';
  @Input() public entity: ActivityEntityName;
  @Input() public entities: ActivityEntityName[];

  @Input() public appendTo: any;
  /**
   * Classes forwarded onto the inner ng-select — pass 'wb' for the design-system look. ng-select
   * copies its host classes onto the dropdown panel, so this styles body-appended panels that
   * host-scoped CSS cannot reach.
   */
  @Input() public selectClass: string;
  @Input() public multiple: boolean;
  @Input() public clearable: boolean;
  @Input() public showDownload: boolean = false;
  @Input() public showView: boolean = false;
  @Input() public downloadContext: DocumentDownloadContext;
  public documentScopeContext = input<DocumentThemeScopeRequest>(null, { alias: 'documentScopeContext' });

  @Output() public download = new EventEmitter<Entity>();
  @Output() public view = new EventEmitter<Attachment>();

  public data: DocumentThemePickerItem[] = [];;
  public PickerItemStyle = PickerItemStyle;
  public shouldGroup = false;
  public isBusy = false;
  public isDisabled = false;

  @ViewChild(NgSelectComponent) private ngSelect: NgSelectComponent;
  private scrollSub?: Subscription;

  constructor(
    injector: Injector,
    private themes: DocumentThemePickerService,
    private downloadService: DocumentDownloadService,
    private cdk: ChangeDetectorRef,
    private elementRef: ElementRef,
    private destroyRef: DestroyRef,
  ) {
    super(injector);
    this.fetch = PickerFetch.Automatic;

    const documentThemeNameMap = new Map(DocumentThemeEntities.map(x => [x._id, x.name]));

    effect(() => {
      let scope = this.documentScopeContext();
      let entity: any = this.entity;

      if (this.entities?.length) {
        entity = 'MultipleEntities';

        this.shouldGroup = true;
        if (!scope) {
          scope = { entities: this.entities, companyIds: [], jobTypes: [], workCategories: [] };
        }
        else {
          scope.entities = this.entities;
        }
      }

      this.themes.getThemesWithScopeRequest(entity, scope)
        .pipe(
          untilDestroyed(this)
        )
        .subscribe(themes => {
          const pickerItem = [];

          for (const theme of themes) {
            const key = theme.entity?.toLowerCase();
            if (documentThemeNameMap.has(key)) {
              pickerItem.push({
                ...theme,
                group: documentThemeNameMap.get(key)
              } as DocumentThemePickerItem);
            }
          }

          this.data = pickerItem;
          this.cdk.markForCheck();
        });


      this.onSelectionChange(this.control?.value);
    });
  }


  public downloadClicked() {
    const theme = this.control.value;

    if (this.downloadContext) {
      if (!this.downloadContext.entity) {
        return;
      }

      let entityName = this.entity;

      // try to get the entity name from the list of theme.
      if (this.shouldGroup || !this.entity) {
        entityName = this.data.find(x => x._id === theme?._id)?.entity;
      }

      this.downloadService.download(entityName, this.downloadContext.entity, theme?._id, this.downloadContext.fileName);
      return;
    }

    this.download.emit(theme);
  }

  public onView() {
    const theme = this.control.value;

    let entityName = this.entity;

    // try to get the entity name from the list of theme.
    if (this.shouldGroup || !this.entity) {
      entityName = this.data.find(x => x._id === theme?._id)?.entity;
    }

    this.isBusy = true;
    this.downloadService.view(entityName, this.downloadContext.entity, theme?._id, this.downloadContext.fileName, () => {
      this.isBusy = false;
      this.cdk.markForCheck();
    })
      .pipe(
        tap(data => {
          if (data) {
            this.isBusy = false;
            this.view.emit({
              title: data.title ?? this.downloadContext.fileName,
              content: data.content.replace('data:application/pdf;base64,', ''),
            });
          }
        }),

      )
      .subscribe();
  }

  protected onSelectionChange(value) {
    this.isDisabled = !value && (this.entities?.includes(ActivityEntityName.WorkOrder) || this.entities?.includes(ActivityEntityName.WorkOrderDispatch));
  }

  // ng-select with appendTo="body" does not reposition when a scrolling ancestor moves;
  // close the panel on ancestor scroll so it does not appear pinned to the viewport
  public onOpen() {
    this.scrollSub?.unsubscribe();
    const targets = this.getScrollableAncestors(this.elementRef.nativeElement);
    this.scrollSub = merge(...targets.map(t => fromEvent(t, 'scroll', { passive: true })))
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.ngSelect?.close());
  }

  public onClose() {
    this.scrollSub?.unsubscribe();
    this.scrollSub = undefined;
  }

  private getScrollableAncestors(el: HTMLElement): (HTMLElement | Window)[] {
    const result: (HTMLElement | Window)[] = [];
    let node = el.parentElement;
    while (node && node !== document.body) {
      const style = getComputedStyle(node);
      if (/(auto|scroll)/.test(style.overflowY + style.overflowX)) {
        result.push(node);
      }
      node = node.parentElement;
    }
    result.push(window);
    return result;
  }
}

@Injectable({ providedIn: 'root' })
export class DocumentThemePickerService {

  constructor(
    private http: HttpClient
  ) { }

  public getThemes(entity: ActivityEntityName): Observable<Entity[]> {
    return this.http.get<Entity[]>(`/api/picker/document-themes/${entity}`)
      .pipe(
        map(x => {
          return Enumerable
            .from(x)
            .orderBy(x => x.name)
            .toArray();
        })
      );
  }

  public getThemesWithEntities(entities: ActivityEntityName[]): Observable<Entity[]> {
    return this.http.post<Entity[]>(`/api/picker/document-themes/all`, entities)
      .pipe(
        map(x => {
          return Enumerable
            .from(x)
            .orderBy(x => x.name)
            .toArray();
        })
      );
  }

  public getThemesWithScopeRequest(entity: ActivityEntityName, request: DocumentThemeScopeRequest): Observable<DocumentThemeAny[]> {
    return this.http.post<DocumentThemeAny[]>(`/api/picker/document-themes/${entity}`, request)
      .pipe(
        map(x => {
          return Enumerable
            .from(x)
            .orderBy(x => x.name)
            .toArray();
        })
      );
  }

  public getDefaultTheme(entity: ActivityEntityName): Observable<Entity> {
    return this.http.get<Entity>(`/api/picker/document-themes/${entity}/default`);
  }

  public getTheme(id: string): Observable<DocumentThemeAny> {
    return this.http.get<DocumentThemeAny>(`/api/picker/document-theme/${id}`);
  }
}

@Injectable({ providedIn: 'root' })
export class DocumentDownloadService {
  private readonly api = '/api/config/merge/download';
  constructor(
    private http: HttpClient,
    private toastService: ToastService,
  ) { }

  public download(entityName: ActivityEntityName, entity: Entity, themeId: string, stringFileName: string): void {
    let fullUrl = `${this.api}/${entityName}/${entity._id}?filename=${stringFileName ?? 'DownloadFile'}`;
    if (themeId) {
      fullUrl += `&themeId=${themeId}`;
    }

    this.http.get(fullUrl, { observe: 'response', responseType: 'blob' })
      .pipe(
        tap(data => {
          const filename = FilenameExtractService.extractFilenameFromResponse(data, `${stringFileName}.pdf`);
          saveAs(data.body as Blob, filename);
        }),
        catchError(onDownloadError(this.toastService, () => EMPTY))
      )
      .subscribe();
  }

  public downloadToFile(entityName: ActivityEntityName, entityId: string, themeId: string, stringFileName: string): Observable<File> {
    let fullUrl = `${this.api}/${entityName}/${entityId}?filename=${stringFileName ?? 'downloadFile.pdf'}`;
    if (themeId) {
      fullUrl += `&themeId=${themeId}`;
    }

    return this.http.get(fullUrl, { observe: 'response', responseType: 'blob' })
      .pipe(
        map(data => {
          const file = new File([data.body as Blob], stringFileName, { type: 'application/pdf' });
          return file;
        }),
        catchError(onDownloadError(this.toastService, () => null))
      )
  }

  public view(entityName: ActivityEntityName, entity: Entity, themeId: string, stringFileName: string, erroCallBack?: () => void): Observable<any> {
    let fullUrl = `${this.api}/${entityName}/${entity._id}?filename=${stringFileName ?? 'DownloadFile'}`;
    if (themeId) {
      fullUrl += `&themeId=${themeId}`;
    }

    return this.http.get(fullUrl, { observe: 'response', responseType: 'blob' }).pipe(
      switchMap((response: HttpResponse<Blob>) => {
        const filename = FilenameExtractService.extractFilenameFromResponse(response, `${stringFileName}.pdf`);
        return new Observable<any>((observer) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            observer.next({
              title: filename,
              content: reader.result as string,
            });
            observer.complete();
          };
          reader.readAsDataURL(response.body);
        });
      }),
      catchError(error => {
        if (erroCallBack) {
          erroCallBack();
        }
        return of(null)
      })
    );
  }
}

@NgModule({
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgSelectModule,
    PickerItemModule,

    ButtonIconComponent,
  ],
  declarations: [
    DocumentThemePickerComponent
  ],
  providers: [],
  exports: [
    DocumentThemePickerComponent
  ],
})
export class DocumentThemePickerModule { }
